import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import type { Serie, CreateSerieDto, UpdateSerieDto, Segmento } from '../services/seriesService';
import { SEGMENTOS } from '../services/seriesService';
import { codigosServicoService } from '../services/codigosServicoService';
import type { CodigoServico } from '../services/codigosServicoService';
import { ValorInput } from './ValorInput';
import { TipoEnsinoSelector } from './TipoEnsinoSelector';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #f1f5f9;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #f1f5f9;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &.error {
    border-color: #ef4444;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  color: #1f2937;
  transition: all 0.2s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ErrorMessage = styled.span`
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 0.25rem;
  display: block;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background-color: #3b82f6;
          border-color: #3b82f6;
          color: white;
          
          &:hover:not(:disabled) {
            background-color: #2563eb;
            border-color: #2563eb;
          }
          
          &:disabled {
            background-color: #9ca3af;
            border-color: #9ca3af;
            cursor: not-allowed;
          }
        `;
      case 'secondary':
        return `
          background-color: white;
          border-color: #d1d5db;
          color: #374151;
          
          &:hover {
            background-color: #f9fafb;
            border-color: #9ca3af;
          }
        `;
      case 'danger':
        return `
          background-color: #ef4444;
          border-color: #ef4444;
          color: white;
          
          &:hover:not(:disabled) {
            background-color: #dc2626;
            border-color: #dc2626;
          }
          
          &:disabled {
            background-color: #9ca3af;
            border-color: #9ca3af;
            cursor: not-allowed;
          }
        `;
    }
  }}

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const LoadingSpinner = styled.div`
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

interface SerieFormModalProps {
  isOpen: boolean;
  serie?: Serie | null;
  onClose: () => void;
  onSubmit: (data: CreateSerieDto | UpdateSerieDto) => Promise<void>;
  existingNames: string[];
}

interface FormData {
  nome: string;
  tipoEnsino: 'curricular' | 'extracurricular' | '';
  segmento: Segmento;
  valorMensalidade: number;
  valorMaterialDidatico: number;
  ordem: number;
  codigoServicoId: number | '';
}

interface FormErrors {
  nome?: string;
  tipo_ensino?: string;
  valor?: string;
  valorMaterialDidatico?: string;
  codigoServicoId?: string;
}

export const SerieFormModal: React.FC<SerieFormModalProps> = ({
  isOpen,
  serie,
  onClose,
  onSubmit,
  existingNames,
}) => {
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    tipoEnsino: '',
    segmento: 'N/A',
    valorMensalidade: 0,
    valorMaterialDidatico: 0,
    ordem: 0,
    codigoServicoId: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codigosServico, setCodigosServico] = useState<CodigoServico[]>([]);
  const [loadingCodigosServico, setLoadingCodigosServico] = useState(false);

  const isEditing = !!serie;

  // Reset form quando o modal abrir/fechar
  useEffect(() => {
    if (isOpen) {
      if (serie) {
        setFormData({
          nome: serie.nome,
          tipoEnsino: serie.tipoEnsino,
          segmento: serie.segmento ?? 'N/A',
          valorMensalidade: serie.valorMensalidade,
          valorMaterialDidatico: serie.valorMaterialDidatico ?? 0,
          ordem: serie.ordem ?? 0,
          codigoServicoId: serie.codigoServicoId ?? '',
        });
      } else {
        setFormData({
          nome: '',
          tipoEnsino: '',
          segmento: 'N/A',
          valorMensalidade: 0,
          valorMaterialDidatico: 0,
          ordem: 0,
          codigoServicoId: '',
        });
      }
      setErrors({});
    }
  }, [isOpen, serie]);

  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      try {
        setLoadingCodigosServico(true);
        const data = await codigosServicoService.list();
        setCodigosServico(data.filter((c) => c.ativo));
      } catch (error) {
        console.error('Erro ao carregar codigos de servico:', error);
      } finally {
        setLoadingCodigosServico(false);
      }
    };

    load();
  }, [isOpen]);

  // Validação do formulário
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validar nome
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    } else if (formData.nome.trim().length < 2) {
      newErrors.nome = 'Nome deve ter pelo menos 2 caracteres';
    } else {
      // Verificar se já existe uma série com mesmo nome e tipo (exceto a atual em edição)
      const nameExists = existingNames.some(existingName => {
        const [name, tipo] = existingName.split('|');
        return (
          name.toLowerCase() === formData.nome.trim().toLowerCase() &&
          tipo === formData.tipoEnsino &&
          (!isEditing || `${serie!.nome}|${serie!.tipoEnsino}` !== existingName)
        );
      });

      if (nameExists) {
        newErrors.nome = `Já existe uma série ${formData.tipoEnsino} com este nome`;
      }
    }

    // Validar tipo de ensino
    if (!formData.tipoEnsino) {
      newErrors.tipo_ensino = 'Tipo de ensino é obrigatório';
    }

    // Validar valor
    if (formData.valorMensalidade <= 0) {
      newErrors.valor = 'Valor deve ser maior que zero';
    } else if (formData.valorMensalidade > 99999.99) {
      newErrors.valor = 'Valor não pode ser superior a R$ 99.999,99';
    }

    if (formData.valorMaterialDidatico < 0) {
      newErrors.valorMaterialDidatico = 'Valor do material didático não pode ser negativo';
    } else if (formData.valorMaterialDidatico > 99999.99) {
      newErrors.valorMaterialDidatico = 'Valor do material didático não pode ser superior a R$ 99.999,99';
    }

    if (!formData.codigoServicoId) {
      newErrors.codigoServicoId = 'Código de serviço é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData: CreateSerieDto | UpdateSerieDto = {
        nome: formData.nome.trim(),
        tipoEnsino: formData.tipoEnsino as 'curricular' | 'extracurricular',
        segmento: formData.segmento,
        valorMensalidade: formData.valorMensalidade,
        ordem: Number(formData.ordem) || 0,
        codigoServicoId: Number(formData.codigoServicoId),
      };

      if (formData.valorMaterialDidatico > 0) {
        submitData.valorMaterialDidatico = formData.valorMaterialDidatico;
      }

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar série:', error);
      // Aqui você pode adicionar tratamento de erro específico
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose} onKeyDown={handleKeyDown}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {isEditing ? '✏️ Editar Série' : '➕ Nova Série'}
          </ModalTitle>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label htmlFor="nome">
                Nome da Série *
              </Label>
              <Input
                id="nome"
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: 1º Ano, Educação Infantil, Curso de Inglês..."
                className={errors.nome ? 'error' : ''}
                maxLength={100}
              />
              {errors.nome && <ErrorMessage>{errors.nome}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <TipoEnsinoSelector
                value={formData.tipoEnsino}
                onChange={(value) => setFormData({ ...formData, tipoEnsino: value })}
                error={errors.tipo_ensino}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="segmento">
                Segmento
              </Label>
              <Select
                id="segmento"
                value={formData.segmento}
                onChange={(e) => setFormData({ ...formData, segmento: e.target.value as Segmento })}
              >
                {SEGMENTOS.map((seg) => (
                  <option key={seg} value={seg}>{seg}</option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <ValorInput
                label="Valor da Mensalidade"
                value={formData.valorMensalidade}
                onChange={(value) => setFormData({ ...formData, valorMensalidade: value })}
                error={errors.valor}
                required
                placeholder="0,00"
              />
            </FormGroup>

            <FormGroup>
              <ValorInput
                label="Valor do Material Didático"
                value={formData.valorMaterialDidatico}
                onChange={(value) => setFormData({ ...formData, valorMaterialDidatico: value })}
                error={errors.valorMaterialDidatico}
                placeholder="0,00"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="ordem">
                Ordem (Posição)
              </Label>
              <Input
                id="ordem"
                type="number"
                value={formData.ordem}
                onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                placeholder="Ex: 1, 2, 3..."
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="codigoServicoId">
                Código de Serviço (NFSe) *
              </Label>
              <Select
                id="codigoServicoId"
                value={formData.codigoServicoId === '' ? '' : String(formData.codigoServicoId)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    codigoServicoId: e.target.value ? Number(e.target.value) : '',
                  })
                }
              >
                <option value="">
                  {loadingCodigosServico ? 'Carregando códigos...' : 'Selecione um código de serviço'}
                </option>
                {codigosServico.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.codigoAtividade} - {c.codigoServico} - {c.descricao}
                  </option>
                ))}
              </Select>
              {errors.codigoServicoId && <ErrorMessage>{errors.codigoServicoId}</ErrorMessage>}
            </FormGroup>
          </ModalBody>

          <ModalFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting && <LoadingSpinner />}
              {isSubmitting
                ? 'Salvando...'
                : isEditing
                  ? 'Atualizar'
                  : 'Criar Série'
              }
            </Button>
          </ModalFooter>
        </form>
      </ModalContainer>
    </ModalOverlay>
  );
};
